/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { Observable } from 'rxjs';
import {
  IManageModePanelFrameFacade,
  MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/manage-mode-panel-frame-facade-interface';
import { ManageModeDestPanelFrameComponent } from '../manage-mode-dest-panel-frame/manage-mode-dest-panel-frame.component';
import { GlobalConstants } from '../../constants/global-constants';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { AlertStatus, IAlert } from '../../../../../../shared/ui/src/lib/components/alert/alert-types';
import { IFamilyInformationItem } from '../add-family-information/add-family-information-types';

@Component({
  selector: 'sysque-manage-mode',
  templateUrl: './manage-mode.component.html',
  styleUrls: ['./manage-mode.component.scss'],
  standalone: false
})
export class ManageModeComponent implements OnInit, OnDestroy {
  @Output() notifyDoneClick: EventEmitter<void> = new EventEmitter();
  @Input() isManageMode: boolean = false;
  public stateCategoryList$: Observable<TabDescription[]>;
  public stateFittingsList$: Observable<Item[]>;
  public sourceStateSystemInfo$: Observable<SystemInfo[]>;
  public sourceStateCategoryList$: Observable<TabDescription[]>;
  public destinationStateSystemInfo$: Observable<SystemInfo[]>;
  public destinationStateCategoryList$: Observable<TabDescription[]>;
  public addFittingMode = true;
  public warningMessage: string = '';
  public showAlert = false;
  public replaceAll = false;
  public showDialog = false;
  public alertStatus = AlertStatus.Warning; 
  public pasteItemInfoList: PastedItemsInfo[] = [];
  public duplicateItemList: PastedItemsInfo[];
  public duplicateListIndex = 0;
  public itemName: string = '';
  public currentItem: Item;
  public srcSelectedSystem: string;
  public destSelectedSystem: string;
  public popUpMsgContent: string;
  public popUpMsgTitle: string;
  public isCopyCategory = false;
  public showHangerAlert = false; 
  public needToExecuteHangerCopyCategory = false;
  public copiedItemList: PastedItemsInfo[] = [];
  public copiedItemInfo: PastedItemsInfo = new PastedItemsInfo();

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }
  
  @ViewChild(ManageModeDestPanelFrameComponent) child: ManageModeDestPanelFrameComponent;

  constructor(
    @Inject(MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN) private facade: IManageModePanelFrameFacade, private cdr: ChangeDetectorRef,
    public copyPasteService: SysQueCopyPasteService
  ) {}

  public ngOnInit(): void {
    this.facade.updateLocalStorage(GlobalConstants.HangerCopySetUp, false);
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.facade.updateLocalStorage(GlobalConstants.CopyCategoryReplaceDuplicate, '');
  }

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
  }

  public onDoneClick(): void {
    this.notifyDoneClick.emit();
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.DuplicateItemsUpdate) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response) {
        this.onShowDuplicateError(this.duplicateItemList);
      }
    }
    if (event.key === GlobalConstants.CopyCategory) {
      if (event.newValue === '' || event.newValue === 'false') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response.status === 'true') {
        this.destSelectedSystem = response.destSystem;
        this.executeCopyCategoryLogic();
      }
    }

    if (event.key === GlobalConstants.ShowHangerSetUp) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response) {
        this.showHangerAlert = response;
        this.showHangerSetUpAlert();
      }
    }

    if (event.key === GlobalConstants.CopyHangerCategoryToSystem) {
      if (JSON.parse(event.newValue) === null) {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response.needToCopy) {
        this.openHangerSpecification(response.hangerItem);
      } else {
        this.getSelectedItemsDetails();
        this.facade.logicToExecuteForHangerCopyCategory(this.copiedItemList, true);

        if (localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
          this.child.updateStatus();
        }
      }
    }
  }

  public showHangerSetUpAlert(): void {
    this.popUpMsgContent = GlobalConstants.HangerSetUpAlertBody;
    this.popUpMsgTitle = GlobalConstants.HangerSetUpAlertTitle;
  }

  public executeCopyCategoryLogic(): void {
    this.isCopyCategory = true;
    this.showDialog = true;
    this.popUpMsgContent =
      GlobalConstants.CopyCategoryAlreadyExistPart +
      ' ' +
      this.destSelectedSystem +
      '.' +
      GlobalConstants.CopyCategoryDoReplaceExistingPart;
    this.popUpMsgTitle = GlobalConstants.CopyCategoryDoReplaceExistingTitle;
  }

  public onPasteError(event: string): void {
    this.warningMessage = event;
    this.alertStatus = AlertStatus.Warning;
    this.showAlert = true;
    this.showDialog = false;
  }

  public onDismissClick(): void {
    this.warningMessage = '';
    this.alertStatus = AlertStatus.Warning;
    this.showAlert = false;
  }

  public onShowDuplicateError(event: PastedItemsInfo[]): void {
    if (this.duplicateItemList && this.duplicateListIndex > this.duplicateItemList.length - 1) {
      this.executePasteLogic();
      return;
    }
    if (!this.duplicateItemList) {
      this.copyPasteService.setDuplicateItemsList([]);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
      this.duplicateItemList = event.filter((x) => x.isDuplicate);
      this.pasteItemInfoList = event;
      this.replaceAll = false;
    }

    this.showAlert = false;
    if (!this.replaceAll) {
      this.getAlertInfo();
    }
  }

  public executePasteLogic(): void {
    this.pasteItemInfoList = this.pasteItemInfoList.filter((item) => !item.isCancel);
    if (this.pasteItemInfoList.length > 0) {
      const item = this.copyPasteService.getUniqueCopiedItemsList();
      if (item.length > 0) {
        item.forEach((item) => {
          this.duplicateItemList.push(item);
        });
      }

      this.facade.executePasteItemsAction(this.duplicateItemList);
      this.cdr.markForCheck();
    }
    if (this.duplicateItemList.length > 0) {
      this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, true);
    }
    this.duplicateItemList = null;
    this.duplicateListIndex = 0;
    this.replaceAll = false;
    if (localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
      this.child.updateStatus();
    }
  }

  public getAlertInfo(): void {
    this.itemName = this.duplicateItemList[this.duplicateListIndex].copiedItem.name;
    this.currentItem = this.duplicateItemList[this.duplicateListIndex].copiedItem;
    this.srcSelectedSystem = this.duplicateItemList[this.duplicateListIndex].srcSystemName;
    this.destSelectedSystem = this.duplicateItemList[this.duplicateListIndex].destSystemName;
    this.showDialog = true;
    this.popUpMsgContent =
      GlobalConstants.CopyCategoryDoReplaceExistingBody + this.itemName + " 'in'" + this.destSelectedSystem + '?';
    this.duplicateListIndex++;
  }

  public onReplaceClick(): void {
    if (this.replaceAll) {
      this.updateItemsOnReplaceAllCheck();
    } else {
      this.replaceLogic();
    }
  }

  private replaceLogic(): void {
    if (this.isCopyCategory) {
      this.showDialog = false;
      this.facade.updateLocalStorage(GlobalConstants.CopyCategoryReplaceDuplicate, true);
      if (!this.showHangerAlert) {
        this.facade.logicToExecuteForCopyCategory(true);
        if (localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
          this.child.updateStatus();
        }
      }
    } else {
      this.updateItemsOnReplaceClick();
    }
  }

  public updateItemsOnReplaceClick(): void {
    this.showDialog = false;
    let indexToUpdate = this.pasteItemInfoList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.pasteItemInfoList[indexToUpdate].isReplace = true;
    this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
    this.onShowDuplicateError(this.duplicateItemList);
  }

  public onCancelClick(): void {
    this.showDialog = false;
    if (this.isCopyCategory) {
      this.facade.updateLocalStorage(GlobalConstants.CopyCategoryReplaceDuplicate, false);
      return;
    }

    let indexToUpdate = this.pasteItemInfoList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.pasteItemInfoList[indexToUpdate].isCancel = true;
    if (this.replaceAll) {
      this.replaceAll = false;
    }
    this.child.removeItem(this.currentItem);
  }

  public onReplaceAll(event: any): void {
    this.replaceAll = event.detail;
  }

  public onCopyCategoryToDestSystem(): void {
    this.child.validateCopyCategory();
  }

  public updateItemsOnReplaceAllCheck(): void {
    this.showDialog = false;
    this.pasteItemInfoList = this.pasteItemInfoList.map((item) => {
      if (item.isDuplicate) {
        return { ...item, isReplaceAll: true };
      } else {
        return item;
      }
    });
    this.executePasteLogic();
  }

  public onNoClick(): void {
    this.showHangerAlert = false;
    this.facade.updateLocalStorage(GlobalConstants.CopyCategoryIsHangerSetUp, false);
    this.getSelectedItemsDetails();
    this.facade.logicToExecuteForHangerCopyCategory(this.copiedItemList, false);
    if (localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
      this.child.updateStatus();
    }
  }

  public onYesClick(): void {
    this.facade.updateLocalStorage(GlobalConstants.CopyCategoryHangerItems, []);
    this.facade.updateLocalStorage(GlobalConstants.CopyHangerCategoryToSystem, null);

    const srcState = this.facade.getManageModeSourceState();
    this.facade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, null);
    this.facade.updateLocalStorage(GlobalConstants.HangerCopySetUp, true);

    let hangerItemList: IFamilyInformationItem[] = srcState.fittingList.filter(
      (item) => item.isHanger
    ) as IFamilyInformationItem[];

    // Add isFromDuplicate property to each item
    hangerItemList.forEach((item) => {
      item.isFromDuplicate = false;
      item.familyCategoryName = 'Pipe Accessories';
      item.parameters= undefined;
      item.pushParameters = '';
    });  
   
    // Open hanger specification for the first item
    if (hangerItemList.length > 0) {
      this.openHangerSpecification(hangerItemList[0]);
    }

    // Remove the first item from the list after processing
    hangerItemList = hangerItemList.slice(1);

    // Update local storage with the modified hangerItemList
    this.facade.updateLocalStorage(GlobalConstants.CopyCategoryHangerItems, hangerItemList);
  }

  public openHangerSpecification(item: IFamilyInformationItem): void {
    item.specId = '';
    item.descriptionInstance = '';
    item.tooltipVisible = false;

    item.systemName = this.facade.getManageModeDestinationState().selectedSystem;
    this.facade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);

    this.facade.openHangerSpecification();
    this.showHangerAlert = false;
  }

  public getSelectedItemsDetails(): void {
    let sourceState = this.facade.getManageModeSourceState();
    let state = this.facade.getManageModeDestinationState();
    const list = sourceState.fittingList;
    this.copiedItemList = [];
    Object.values(list).forEach((item) => {
      this.copiedItemInfo = new PastedItemsInfo();
      this.copiedItemInfo.copiedItem = item;
      this.copiedItemInfo.srcSystemName = sourceState.selectedSystem;
      this.copiedItemInfo.destSystemName = state.selectedSystem;
      this.copiedItemInfo.category = sourceState.selectedCategory;
      this.copiedItemInfo.destCategory = state.selectedCategory;
      this.copiedItemInfo.isDestCustomContent = state.categoryList.filter(
        (category) => category.tabName == state.selectedCategory
      )[0]?.isCustomContent;
      this.copiedItemList.push(this.copiedItemInfo);
    });
  } 
}  